import { useId } from 'react'

import * as Checkbox from '@radix-ui/react-checkbox'
import { Controller } from 'react-hook-form'

import IconCheck from '@/assets/icons/icon-check.svg'
import { ErrorText } from '@/components/ui/error-text'
import type { Weekday } from '@/modules/tasks/service/repeatable-task-service.ts'
import { weekdays } from '@/modules/tasks/service/repeatable-task-service.ts'

function addToSet<T>(set: T[], item: T) {
  return set.includes(item) ? set : [...set, item]
}

function removeFromSet<T>(set: T[], item: T) {
  return set.filter((it) => it !== item)
}

interface Props {
  name: string
  entityLabel: string
  showError?: boolean
}

export const WeeklyRepetitionSelector = ({
  name,
  entityLabel,
  showError,
}: Props) => {
  const baseId = useId()

  return (
    <Controller
      name={name}
      render={({ field, fieldState: { error } }) => (
        <>
          <div className="grid grid-cols-7 gap-2">
            {weekdays.map((day) => (
              <Checkbox.Root
                key={day}
                className="col-span-1 flex h-16 cursor-pointer flex-col items-center gap-2 rounded-lg bg-secondary-50 p-2 transition hover:bg-secondary-100 dark:bg-secondary-500/40 dark:hover:bg-secondary-500/60"
                id={`${baseId}-${day}`}
                checked={field.value.includes(day)}
                onCheckedChange={(checked) => {
                  if (checked) {
                    field.onChange(addToSet(field.value, day))
                  } else {
                    field.onChange(removeFromSet(field.value, day))
                  }
                }}
              >
                <label
                  htmlFor={`${baseId}-${day}`}
                  className="text-xs font-semibold uppercase text-title"
                >
                  {day.substring(0, 3)}
                </label>
                <Checkbox.Indicator className="relative flex h-full w-full items-center justify-center rounded-full bg-primary-500 py-1 text-white">
                  <IconCheck className="h-4 w-4" />
                </Checkbox.Indicator>
              </Checkbox.Root>
            ))}
          </div>
          {showError && error ? (
            <ErrorText>{error.message}</ErrorText>
          ) : (
            <span className="block text-xs text-secondary-300">
              <WeeklyScheduleNote
                days={field.value}
                entityLabel={entityLabel}
              />
            </span>
          )}
        </>
      )}
    />
  )
}

const WeeklyScheduleNote = ({
  days,
  entityLabel,
}: {
  days: Weekday[]
  entityLabel: string
}) => {
  if (days.length === 0) {
    return <>No day is selected</>
  }
  if (days.length === weekdays.length) {
    return <>The {entityLabel} will be scheduled every day</>
  }
  if (days.length > 0) {
    return (
      <>
        The {entityLabel} will be scheduled every{' '}
        {joinWithAnd(sortByWeekdays(days))}
      </>
    )
  }
}

function sortByWeekdays(days: Weekday[]) {
  return days.sort((a, b) => weekdays.indexOf(a) - weekdays.indexOf(b))
}

function joinWithAnd(items: string[]) {
  if (items.length === 1) {
    return items[0]
  }

  return items.slice(0, -1).join(', ') + ' and ' + items[items.length - 1]
}
